import { FieldArray } from "formik";
import CalculatedCurrencyCell from "./CalculatedCurrenyCell";
import { useState } from "react";
import { getFieldArrayName } from "../utils/FinancesTableUtils";
import FinancesTableCell from "./FinancesTableCell";
import {
  FinancesTableProps,
  TableColumn,
} from "../types/financesTableComponentTypes";
import FinancesTableBonusCell from "./FinancesTableBonusCell";

const FinancesTable = ({
  data,
  columns,
  title,
  calculateEstimated,
  calculateActual,
  calculateDifference,
  referenceSelf = false,
  invertColors = false,
  singular = false,
  fieldArrayName,
  blankObjectValue,
  showCalculated = true,
  estimatedColumnName = "Estimated",
}: FinancesTableProps) => {
  const [isThresholdPercentMode, setIsThresholdPercentMode] = useState<
    Record<number, boolean>
  >({});
  const [isAwardPercentMode, setIsAwardPercentMode] = useState<
    Record<number, boolean>
  >({});
  const headers: { [key: string]: string | null } = {
    estimated: estimatedColumnName,
    actual: "Actual",
    difference: "Difference",
  };
  const headerColors = [
    "bg-[#6D53FD]",
    "bg-[#19AEDE]",
    "bg-gray-100",
    "bg-white",
  ];

  const getTotalEstimated = (): number => {
    return data.reduce((sum: number, item: T) => {
      if (!calculateEstimated) return sum;
      return sum + calculateEstimated(item);
    }, 0);
  };

  const getTotalActual = (): number => {
    return data.reduce(
      (sum: number, item: any) =>
        sum + (calculateActual ? calculateActual(item) : 0),
      0
    );
  };

  const getTotalDiffPercentage = () => {
    const totalEstimated = getTotalEstimated();
    const totalActual = getTotalActual();
    if (!totalEstimated) return 0;
    return totalActual - totalEstimated;
  };

  if (!data?.map) {
    return null;
  }

  return (
    <FieldArray name={fieldArrayName}>
      {({ push, remove, form }) => {
        return (
          <div className="flex flex-col min-w-0 px-4 pt-1 bg-white">
            <h1 className="font-bold text-lg mb-1 text-[#6D53FD]">{title}</h1>
            <div className="flex min-w-0 flex-row">
              {/*left side*/}
              <div className="flex-[6]">
                <div className="w-full overflow-x-hidden">
                  <table className="table-auto w-full">
                    <thead>
                      <tr className="flex min-w-0">
                        {columns.map((column, index) => (
                          <th
                            key={index}
                            className="px-2 h-10 text-left overflow-hidden text-black text-sm bg-white flex-1"
                          >
                            {column.title}
                          </th>
                        ))}
                      </tr>
                    </thead>

                    <tbody>
                      {data.map((item: any, index: number) => (
                        <tr key={index} className="flex min-w-0">
                          {columns.map(
                            (column: TableColumn, colIndex: number) => (
                              <>
                                {(column.type === "threshold" ||
                                  column.type === "award") && (
                                  <>
                                    <FinancesTableBonusCell
                                      key={colIndex}
                                      fieldNameFlat={getFieldArrayName(
                                        column,
                                        index,
                                        fieldArrayName,
                                        singular,
                                        referenceSelf,
                                        false,
                                        false
                                      )}
                                      fieldNamePercent={getFieldArrayName(
                                        column,
                                        index,
                                        fieldArrayName,
                                        singular,
                                        referenceSelf,
                                        true,
                                        true
                                      )}
                                      form={form}
                                    />
                                  </>
                                )}
                                {column.type !== "threshold" &&
                                  column.type !== "award" && (
                                    <FinancesTableCell
                                      key={colIndex}
                                      colIndex={colIndex}
                                      column={column}
                                      index={index}
                                      fieldName={getFieldArrayName(
                                        column,
                                        index,
                                        fieldArrayName,
                                        singular,
                                        referenceSelf
                                        // isThresholdPercentMode[index],
                                        // isAwardPercentMode[index]
                                      )}
                                      // isThresholdPercentMode={
                                      //   isThresholdPercentMode
                                      // }
                                      // isAwardPercentMode={isAwardPercentMode}
                                      // setIsThresholdPercentMode={
                                      //   setIsThresholdPercentMode
                                      // }
                                      // setIsAwardPercentMode={
                                      //   setIsAwardPercentMode
                                      // }
                                    />
                                  )}
                              </>
                            )
                          )}
                        </tr>
                      ))}

                      {!singular && (
                        <>
                          <tr>
                            <td className="px-2 h-10" colSpan={columns.length}>
                              <button
                                type="button"
                                className="text-gray-500 cursor-pointer"
                                onClick={() => {
                                  push(blankObjectValue);
                                }}
                              >
                                + Add item
                              </button>
                            </td>
                          </tr>
                          {showCalculated && (
                            <tr>
                              <td
                                className="px-2 h-10 bg-gray-100"
                                colSpan={columns.length}
                              >
                                Total
                              </td>
                            </tr>
                          )}
                        </>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/*right side*/}
              {showCalculated && (
                <div className="flex-[4]">
                  <div className="overflow-x-hidden">
                    <table className="table-auto w-full">
                      <thead>
                        <tr className="flex min-w-0">
                          {Object.entries(headers).map(
                            ([_key, header], index) => (
                              <th
                                key={index}
                                className={`flex-[3] px-2 py-2 text-left overflow-hidden h-10 ${
                                  headerColors[index]
                                } ${
                                  index === 2 ? "text-black" : "text-white"
                                } text-m  text-center`}
                              >
                                {header}
                              </th>
                            )
                          )}
                          <td className="flex-[1] px-2 h-10 bg-white"></td>
                        </tr>
                      </thead>
                      <tbody>
                        {data.map((item: any, index: number) => (
                          <tr key={index} className="flex min-w-0">
                            <td className="flex-[3] px-2 h-10 bg-[#F1E5FF] text-center">
                              {!calculateEstimated ? (
                                <div className="text-center">-</div>
                              ) : (
                                <CalculatedCurrencyCell
                                  value={calculateEstimated(item) ?? 0}
                                  name={`${fieldArrayName}[${index}].estimated`}
                                  isPercentage={false}
                                />
                              )}
                            </td>
                            <td className="flex-[3] px-2 h-10 bg-[#E2F4FC] text-center">
                              <CalculatedCurrencyCell
                                value={calculateActual(item)}
                                name={`${fieldArrayName}[${index}].actual`}
                                isPercentage={false}
                              />
                            </td>
                            <td className="flex-[3] px-2 h-10 bg-white text-center">
                              {calculateDifference && (
                                <CalculatedCurrencyCell
                                  value={calculateDifference(item)}
                                  name={`${fieldArrayName}[${index}].difference`}
                                  invertColors={invertColors}
                                  isDiff={true}
                                />
                              )}
                            </td>

                            {singular ? (
                              <td className="flex-[1] px-2 h-10 text-center cursor-pointer hover:bg-gray-100 active:scale-95 transition-all bg-white text-black" />
                            ) : (
                              <td
                                className="flex-[1] px-2 h-10 text-center transition-all bg-white text-black cursor-pointer"
                                onClick={() => {
                                  remove(index);
                                  form.handleSubmit();
                                }}
                              >
                                -
                              </td>
                            )}
                          </tr>
                        ))}

                        {!singular && (
                          <>
                            <tr className="flex min-w-0">
                              <td className="flex-[3] px-2 h-10 bg-[#F1E5FF]"></td>
                              <td className="flex-[3] px-2 h-10 bg-[#E2F4FC]"></td>
                              <td className="flex-[3] px-2 h-10 bg-white"></td>
                              <td className="flex-[1] px-2 h-10 bg-white"></td>
                            </tr>

                            <tr className="flex font-bold">
                              <td className="flex-[3] px-2 h-10 bg-[#6D53FD] text-white text-center">
                                {!calculateEstimated ? (
                                  <div className="text-center">-</div>
                                ) : (
                                  <CalculatedCurrencyCell
                                    value={data.reduce(
                                      (sum: number, item: number) =>
                                        sum + calculateEstimated(item),
                                      0
                                    )}
                                    name={`${name}.total.estimated`}
                                    textColor="text-white"
                                    isPercentage={false}
                                  />
                                )}
                              </td>
                              <td className="flex-[3] px-2 h-10 bg-[#19AEDE] text-white text-center">
                                <CalculatedCurrencyCell
                                  value={data.reduce(
                                    (sum: number, item: number) =>
                                      sum +
                                      (calculateActual
                                        ? calculateActual(item)
                                        : 0),
                                    0
                                  )}
                                  name={`${name}.total.actual`}
                                  textColor="text-white"
                                  isPercentage={false}
                                />
                              </td>
                              <td className="flex-[3] px-2 h-10 bg-gray-100 text-center">
                                <CalculatedCurrencyCell
                                  value={getTotalDiffPercentage()}
                                  name={`${name}.total.difference`}
                                  invertColors={invertColors}
                                  isDiff={true}
                                />
                              </td>
                              <td className="flex-[1] px-2 h-10 bg-white"></td>
                            </tr>
                          </>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          </div>
        );
      }}
    </FieldArray>
  );
};

export default FinancesTable;
